import {
  contactChannelDefinitionByValue,
  displayContactChannelLabel,
} from "../../../../../_shared/_logic/outreach-presentation"
import type {
  ContactOrderPlanningDigitalChannelTag,
  ContactOrderPlanningPostalOption,
  ContactOrderPlanningPreview,
  ContactOrderPostalSelection,
} from "../../../../../api/kehrblatt/contact-orders"
import type { ContactChannel } from "../../../../../_shared/_logic/enums"
import {
  buildRichTooltip,
  tooltipField,
  tooltipTitle,
} from "../../../../../_shared/_logic/rich-tooltips"
import {
  CONTACT_ORDER_DIGITAL_CHANNELS,
  type ContactOrderAppointmentRow,
  type ContactOrderChannelToggle,
  type ContactOrderChannelValue,
  type ContactOrderDigitalChannel,
  type ContactOrderDigitalPlan,
  type ContactOrderPostalToggleOption,
  type ContactOrderRecipientTag,
} from "../planning-selection-contract"
import { buildAnnouncementTimingPresentation, formatPreviewDueAt } from "./announcementTiming"

const DIGITAL_CHANNEL_ORDER: ContactOrderDigitalChannel[] = [...CONTACT_ORDER_DIGITAL_CHANNELS]
export const MAX_DIGITAL_RECIPIENTS_PER_CHANNEL = 2
const DIGITAL_RECIPIENT_LIMIT_DISABLED_REASON = "Maximal 2 Ansprechpartner pro Kontaktkanal."

const normalizeDigitalChannel = (value: unknown): ContactOrderDigitalChannel | null => {
  const normalized = `${value ?? ""}`.trim().toUpperCase()

  return CONTACT_ORDER_DIGITAL_CHANNELS.includes(normalized as ContactOrderDigitalChannel)
    ? (normalized as ContactOrderDigitalChannel)
    : null
}

export const normalizeDigitalChannels = (channels?: unknown): ContactOrderDigitalChannel[] => {
  if (!Array.isArray(channels)) return []

  const selected = new Set(
    channels
      .map(normalizeDigitalChannel)
      .filter((channel): channel is ContactOrderDigitalChannel => Boolean(channel)),
  )

  return DIGITAL_CHANNEL_ORDER.filter((channel) => selected.has(channel))
}

export const normalizeDigitalPlan = (plan?: unknown): ContactOrderDigitalPlan => {
  if (!plan || typeof plan !== "object" || Array.isArray(plan)) return {}

  const normalized: ContactOrderDigitalPlan = {}
  for (const [rawCustomerId, rawChannels] of Object.entries(plan)) {
    const customerId = `${rawCustomerId ?? ""}`.trim()
    const channels = normalizeDigitalChannels(rawChannels)

    if (customerId && channels.length) normalized[customerId] = channels
  }

  return normalized
}

const valuesForRecipientChannel = (
  recipient: ContactOrderRecipientTag,
  channel: ContactOrderDigitalChannel,
) => recipient.channelValues.filter((entry) => entry.channel === channel)

const uniqueValues = (values: ContactOrderChannelValue[]) => {
  const seen = new Set<string>()

  return values.filter((entry) => {
    const key = `${entry.customerId}:${entry.channel}:${entry.value}:${entry.isVerified ? "verified" : "unverified"}`

    if (seen.has(key)) return false

    seen.add(key)

    return true
  })
}

const digitalPreviewTags = (preview?: ContactOrderPlanningPreview | null) =>
  preview?.digital_channel_tags ?? []

const previewTagKey = (customerId: string, channel: ContactOrderDigitalChannel) =>
  `${customerId}:${channel}`

const previewTagsByKey = (preview?: ContactOrderPlanningPreview | null) =>
  new Map(
    digitalPreviewTags(preview).map((tag) => [previewTagKey(tag.customer_id, tag.channel), tag]),
  )

const hasPreviewDigitalProjection = (row: ContactOrderAppointmentRow) =>
  Array.isArray(row.planningPreview?.digital_channel_tags)

const channelAvailableForRecipient = ({
  row,
  previewTags,
  recipient,
  channel,
}: {
  row: ContactOrderAppointmentRow
  previewTags: Map<string, ContactOrderPlanningDigitalChannelTag>
  recipient: ContactOrderRecipientTag
  channel: ContactOrderDigitalChannel
}) =>
  hasPreviewDigitalProjection(row)
    ? previewTags.has(previewTagKey(recipient.customerId, channel))
    : valuesForRecipientChannel(recipient, channel).length > 0

const digitalRecipientCount = (
  plan: ContactOrderDigitalPlan,
  channel: ContactOrderDigitalChannel,
) => Object.values(plan).filter((channels) => channels.includes(channel)).length

export const digitalChannelLimitDisabledReason = ({
  digitalPlan,
  customerId,
  channel,
}: {
  digitalPlan: ContactOrderDigitalPlan
  customerId: string
  channel: ContactOrderDigitalChannel
}) => {
  if (digitalPlan[customerId]?.includes(channel)) return null

  return digitalRecipientCount(digitalPlan, channel) >= MAX_DIGITAL_RECIPIENTS_PER_CHANNEL
    ? DIGITAL_RECIPIENT_LIMIT_DISABLED_REASON
    : null
}

const limitDigitalRecipients = (
  row: ContactOrderAppointmentRow,
  plan: ContactOrderDigitalPlan,
): ContactOrderDigitalPlan => {
  const counts = new Map<ContactOrderDigitalChannel, number>()
  const result: ContactOrderDigitalPlan = {}
  for (const recipient of row.recipients) {
    const channels = plan[recipient.customerId] ?? []
    const accepted: ContactOrderDigitalChannel[] = []
    for (const channel of DIGITAL_CHANNEL_ORDER.filter((entry) => channels.includes(entry))) {
      const count = counts.get(channel) ?? 0

      if (count >= MAX_DIGITAL_RECIPIENTS_PER_CHANNEL) continue

      counts.set(channel, count + 1)
      accepted.push(channel)
    }

    if (accepted.length) result[recipient.customerId] = accepted
  }

  return result
}

export const filterDigitalPlanForContactOrderRow = (
  row: ContactOrderAppointmentRow,
  plan: ContactOrderDigitalPlan,
  allowedChannels: readonly string[],
): ContactOrderDigitalPlan => {
  const allowed = new Set(normalizeDigitalChannels(allowedChannels))
  const recipientById = new Map(
    row.recipients.map((recipient) => [recipient.customerId, recipient]),
  )
  const previewTags = previewTagsByKey(row.planningPreview)
  const filtered: ContactOrderDigitalPlan = {}
  for (const [customerId, channels] of Object.entries(normalizeDigitalPlan(plan))) {
    const recipient = recipientById.get(customerId)

    if (!recipient) continue

    const available = channels.filter(
      (channel) =>
        allowed.has(channel) &&
        channelAvailableForRecipient({ row, previewTags, recipient, channel }),
    )

    if (available.length) filtered[customerId] = available
  }

  return limitDigitalRecipients(row, filtered)
}

export const availableDigitalChannelsForContactOrderRow = (
  row: ContactOrderAppointmentRow,
  customerIds: string[],
  allowedChannels: readonly string[],
) => {
  const allowed = new Set(normalizeDigitalChannels(allowedChannels))
  const selectedIds = new Set(customerIds)
  const available = new Set<ContactOrderDigitalChannel>()

  if (hasPreviewDigitalProjection(row)) {
    for (const tag of digitalPreviewTags(row.planningPreview)) {
      if (selectedIds.has(tag.customer_id) && allowed.has(tag.channel)) available.add(tag.channel)
    }
  } else {
    for (const recipient of row.recipients) {
      if (!selectedIds.has(recipient.customerId)) continue

      for (const channel of DIGITAL_CHANNEL_ORDER) {
        if (allowed.has(channel) && valuesForRecipientChannel(recipient, channel).length) {
          available.add(channel)
        }
      }
    }
  }

  return DIGITAL_CHANNEL_ORDER.filter((channel) => available.has(channel))
}

export const manualDigitalPlanForContactOrderRow = (
  row: ContactOrderAppointmentRow,
  customerIds: string[],
  channels: unknown,
  allowedChannels: readonly string[],
) => {
  const selectedChannels = normalizeDigitalChannels(channels)
  const plan = Object.fromEntries(customerIds.map((customerId) => [customerId, selectedChannels]))

  return filterDigitalPlanForContactOrderRow(row, plan, allowedChannels)
}

const postalOptionValue = (
  mode: ContactOrderPostalSelection["mode"],
  customerId?: string | null,
  contactPointId?: string | null,
  addressSource?: string | null,
) =>
  mode === "PERSONAL" && customerId
    ? `PERSONAL:${customerId}${addressSource ? `:${contactPointId ?? "object"}` : ""}`
    : mode

export const postalSelectionValue = (selection: ContactOrderPostalSelection) =>
  selection.manual_recipient
    ? "INDIVIDUAL"
    : postalOptionValue(
        selection.mode,
        selection.customer_id,
        selection.contact_point_id,
        selection.address_source,
      )

export const postalSelectionFromValue = (
  value: string,
  options: ContactOrderPostalToggleOption[] = [],
): ContactOrderPostalSelection | null => {
  const option = options.find((option) => option.value === value)

  if (option?.selection) return { ...option.selection }

  if (value === "DISABLED") return { mode: "DISABLED" }

  if (value === "IMPERSONAL") return { mode: "IMPERSONAL" }

  if (value === "NOTICE") return { mode: "NOTICE", dispatch_mode: "MANUAL" }

  if (!value.startsWith("PERSONAL:")) return null

  const [customerId, point] = value.slice("PERSONAL:".length).trim().split(":")

  return customerId
    ? {
        mode: "PERSONAL",
        customer_id: customerId,
        ...(point
          ? {
              address_source:
                point === "object" ? ("OBJECT_ADDRESS" as const) : ("CONTACT_POINT" as const),
              contact_point_id: point === "object" ? null : point,
            }
          : {}),
      }
    : null
}

const fallbackPostalOptions = (
  row: ContactOrderAppointmentRow,
  allowedChannels: readonly string[],
): ContactOrderPlanningPostalOption[] => {
  const letterAllowed = allowedChannels.includes("LETTER")

  return [
    {
      mode: "DISABLED",
      customer_id: null,
      label: "Inaktiv",
      available: true,
    },
    {
      mode: "IMPERSONAL",
      customer_id: null,
      label: "Unpersönlich",
      available: letterAllowed,
      disabled_reason: letterAllowed
        ? null
        : "Terminbriefe sind für diese Organisation nicht verfügbar.",
    },
    ...row.recipients.map((recipient) => ({
      mode: "PERSONAL" as const,
      customer_id: recipient.customerId,
      label: recipient.nameLabel,
      sub_label: recipient.roleLabel ?? null,
      available: letterAllowed,
      disabled_reason: letterAllowed
        ? null
        : "Terminbriefe sind für diese Organisation nicht verfügbar.",
    })),
  ]
}

const postalOptions = (row: ContactOrderAppointmentRow, allowedChannels: readonly string[]) =>
  row.planningPreview?.postal_options?.length
    ? row.planningPreview.postal_options
    : fallbackPostalOptions(row, allowedChannels)

const optionForSelection = (
  options: ContactOrderPlanningPostalOption[],
  selection: ContactOrderPostalSelection,
) =>
  options.find(
    (option) =>
      option.mode === selection.mode &&
      (selection.mode !== "PERSONAL" || option.customer_id === selection.customer_id),
  )

export const reconcilePostalSelectionForContactOrderRow = (
  row: ContactOrderAppointmentRow,
  selection: ContactOrderPostalSelection,
  allowedChannels: readonly string[],
): ContactOrderPostalSelection => {
  // Preserve explicit postal decisions across digital changes and refreshes.
  // A stale selection must be corrected, never silently replaced.
  return selection
}

export const buildPostalToggleOptionsForContactOrderRow = (
  row: ContactOrderAppointmentRow,
  allowedChannels: readonly string[],
): ContactOrderPostalToggleOption[] =>
  postalOptions(row, allowedChannels).map((option) => ({
    value: postalOptionValue(
      option.mode,
      option.customer_id,
      option.contact_point_id,
      option.address_source,
    ),
    mode: option.mode,
    customerId: option.customer_id,
    selectedLabel:
      option.mode === "PERSONAL"
        ? [option.sub_label, option.label].filter(Boolean).join(": ")
        : undefined,
    label:
      option.mode === "DISABLED"
        ? "Kein Terminbrief"
        : option.mode === "IMPERSONAL"
          ? "Brief an Objektadresse – ohne Namen"
          : option.mode === "NOTICE"
            ? "Aushang"
            : [option.sub_label, option.label].filter(Boolean).join(": ") +
              (option.recipient_address
                ? ` – ${option.address_source === "OBJECT_ADDRESS" ? "Objektadresse: " : ""}${option.recipient_address.street} ${option.recipient_address.house_number}, ${option.recipient_address.city}`
                : ""),
    disabled: !option.available,
    tooltip: option.disabled_reason ?? option.tooltip ?? option.sub_label,
    address: option.recipient_address,
    selection: {
      mode: option.mode,
      customer_id: option.customer_id,
      address_source: option.address_source,
      contact_point_id: option.contact_point_id,
      recipient_revision: option.recipient_revision,
    },
  }))

const contactValuesTooltip = (
  channel: ContactOrderDigitalChannel,
  values: ContactOrderChannelValue[],
) =>
  buildRichTooltip([
    tooltipTitle(displayContactChannelLabel(channel)),
    ...uniqueValues(values).map((entry) =>
      tooltipField(
        entry.customerLabel,
        `${entry.value} (${entry.isVerified ? "verifiziert" : "nicht verifiziert"})`,
      ),
    ),
  ])

export const buildDigitalChannelTogglesForContactOrderRow = ({
  row,
  digitalPlan,
  allowedChannels,
  pending = false,
}: {
  row: ContactOrderAppointmentRow
  digitalPlan: ContactOrderDigitalPlan
  allowedChannels: readonly string[]
  pending?: boolean
}): ContactOrderChannelToggle[] => {
  const allowed = new Set(normalizeDigitalChannels(allowedChannels))
  const previewTags = previewTagsByKey(row.planningPreview)
  const toggles: ContactOrderChannelToggle[] = []
  for (const recipient of row.recipients) {
    for (const channel of DIGITAL_CHANNEL_ORDER) {
      if (!allowed.has(channel)) continue

      const values = valuesForRecipientChannel(recipient, channel)
      const previewTag = previewTags.get(previewTagKey(recipient.customerId, channel))

      if (hasPreviewDigitalProjection(row) ? !previewTag : !values.length) continue

      const active = Boolean(digitalPlan[recipient.customerId]?.includes(channel))
      const roleLabel = previewTag?.role_label ?? recipient.roleLabel ?? "Rolle nicht hinterlegt"
      const timing = buildAnnouncementTimingPresentation({
        label: channel === "PHONE" ? "Automatischer Anruf" : displayContactChannelLabel(channel),
        active,
        pending,
        planned: Boolean(previewTag?.planned),
        dueAt: previewTag?.due_at,
        previewError: row.planningPreviewError,
        reasonCode:
          previewTag?.reason_code === "skipped_after_deadline"
            ? previewTag.reason_code
            : row.planningPreview?.channel_tags?.find(
                (tag) => tag.label === displayContactChannelLabel(channel),
              )?.reason_code,
      })
      const followUp =
        channel === "EMAIL" &&
        active &&
        !pending &&
        !row.planningPreviewError &&
        previewTag?.planned
          ? formatPreviewDueAt(previewTag.follow_up_due_at)
          : null
      const contactValues = [...new Set(values.map((entry) => entry.value.trim()).filter(Boolean))]
      toggles.push({
        key: `${recipient.customerId}:${channel}`,
        channel,
        customerId: recipient.customerId,
        recipientLabel: recipient.nameLabel,
        label: contactValues.join(", ") || "Kontaktwert nicht verfügbar",
        subLabel: `${roleLabel}: ${recipient.nameLabel}`,
        topLabel: timing.topLabel,
        active,
        color: timing.color,
        icon: contactChannelDefinitionByValue[channel as ContactChannel]?.icon ?? null,
        tooltip: previewTag
          ? [
              timing.topLabel,
              `${roleLabel}: ${recipient.nameLabel}`,
              followUp ? `Erinnerung geplant ab ${followUp}` : null,
            ]
              .filter(Boolean)
              .join(". ")
          : contactValuesTooltip(channel, values),
        tooltipHtml: !previewTag,
        disabledReason: digitalChannelLimitDisabledReason({
          digitalPlan,
          customerId: recipient.customerId,
          channel,
        }),
      })
    }
  }

  return toggles
}
